import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Camera, CheckCircle2, Minus, XCircle } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type ChecklistItem,
  type InspectionChecklist,
  isInspectionChecklistReadOnly,
  useChecklistItems,
  useInspectionChecklist,
  useSignOffInspection,
  useUpdateChecklistItem,
} from "@/hooks/use-inspections";
import { formatDateOnlyLocal } from "@/lib/utils";

type ItemResponse = "pass" | "fail" | "na";

interface LocalItemState {
  response: ItemResponse | null;
  notes: string;
}

function statusVariant(
  status: InspectionChecklist["status"],
): "default" | "warning" | "success" | "destructive" | "secondary" {
  switch (status) {
    case "not_started":
      return "default";
    case "in_progress":
      return "warning";
    case "completed":
      return "success";
    case "failed":
      return "destructive";
    case "void":
      return "secondary";
  }
}

function statusLabel(status: InspectionChecklist["status"]): string {
  switch (status) {
    case "not_started":
      return "Not Started";
    case "in_progress":
      return "In Progress";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "void":
      return "Void";
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "--";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function responseToLocal(response: string | null): ItemResponse | null {
  if (response === "pass") return "pass";
  if (response === "fail") return "fail";
  if (response === "na") return "na";
  return null;
}

function ChecklistItemCard({
  item,
  localState,
  onResponse,
  onNotes,
  updatePending,
  readOnly,
}: {
  item: ChecklistItem;
  localState: LocalItemState;
  onResponse: (response: ItemResponse) => void;
  onNotes: (notes: string) => void;
  updatePending: boolean;
  readOnly: boolean;
}) {
  const isFail = localState.response === "fail";
  const disabled = updatePending || readOnly;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <span className="text-xs text-muted-foreground shrink-0 w-5 pt-0.5">
            {item.itemOrder}.
          </span>
          <div className="flex-1 space-y-3">
            <p className="text-sm font-medium leading-snug">
              {item.description}
              {item.isRequired && <span className="text-destructive ml-1">*</span>}
            </p>

            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={localState.response === "pass" ? "default" : "outline"}
                className={`min-h-[44px] flex-1 gap-1.5 ${
                  localState.response === "pass"
                    ? "bg-green-600 hover:bg-green-700 border-green-600 text-white"
                    : "border-green-200 text-green-700 hover:bg-green-50 hover:border-green-400"
                }`}
                onClick={() => onResponse("pass")}
                disabled={disabled}
              >
                <CheckCircle2 className="h-4 w-4" />
                Pass
              </Button>

              <Button
                type="button"
                size="sm"
                variant={localState.response === "fail" ? "default" : "outline"}
                className={`min-h-[44px] flex-1 gap-1.5 ${
                  localState.response === "fail"
                    ? "bg-destructive hover:bg-destructive/90 border-destructive text-destructive-foreground"
                    : "border-red-200 text-red-700 hover:bg-red-50 hover:border-red-400"
                }`}
                onClick={() => onResponse("fail")}
                disabled={disabled}
              >
                <XCircle className="h-4 w-4" />
                Fail
              </Button>

              <Button
                type="button"
                size="sm"
                variant={localState.response === "na" ? "secondary" : "outline"}
                className="min-h-[44px] flex-1 gap-1.5"
                onClick={() => onResponse("na")}
                disabled={disabled}
              >
                <Minus className="h-4 w-4" />
                N/A
              </Button>
            </div>

            {isFail && (
              <div className="space-y-2 pt-1">
                {readOnly ? (
                  localState.notes.trim() ? (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap border rounded-md p-3 bg-muted/30">
                      {localState.notes}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      No failure notes recorded.
                    </p>
                  )
                ) : (
                  <Textarea
                    placeholder="Describe the issue (required on Fail)..."
                    value={localState.notes}
                    onChange={(e) => onNotes(e.target.value)}
                    className="text-sm min-h-[80px] resize-none"
                    disabled={disabled}
                  />
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] w-full gap-2 text-muted-foreground"
                  disabled
                >
                  <Camera className="h-4 w-4" />
                  Add Photo (coming soon)
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SignOffDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Sign Off Inspection</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            This completes the checklist and records your account and sign-off time.
          </p>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button
            variant="outline"
            className="min-h-[44px]"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button className="min-h-[44px]" disabled={isPending} onClick={onConfirm}>
            {isPending ? "Saving..." : "Complete Sign-Off"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InspectionDetailPage() {
  const navigate = useNavigate();
  const rawParams = useParams({ strict: false });
  const id = typeof rawParams.id === "string" ? rawParams.id : "";

  const {
    data: checklist,
    isLoading: checklistLoading,
    isError: checklistError,
    error: checklistErr,
    refetch: refetchChecklist,
  } = useInspectionChecklist(id, id.length > 0);

  const {
    data: items,
    isLoading: itemsLoading,
    isError: itemsError,
    error: itemsErr,
    refetch: refetchItems,
  } = useChecklistItems(id, id.length > 0);

  const updateItem = useUpdateChecklistItem();
  const signOff = useSignOffInspection();

  const [localStates, setLocalStates] = useState<Record<string, LocalItemState>>({});
  const [signOffOpen, setSignOffOpen] = useState(false);

  if (!id) {
    return (
      <ErrorState message="Missing inspection ID. Open this page from the inspections list." />
    );
  }

  function getLocalState(item: ChecklistItem): LocalItemState {
    return (
      localStates[item.id] ?? {
        response: responseToLocal(item.response),
        notes: item.notes ?? "",
      }
    );
  }

  function handleResponse(item: ChecklistItem, response: ItemResponse) {
    if (checklist && isInspectionChecklistReadOnly(checklist.status)) return;
    const current = getLocalState(item);
    const next: LocalItemState = { ...current, response };
    setLocalStates((prev) => ({ ...prev, [item.id]: next }));
    updateItem.mutate({
      id: item.id,
      inspectionChecklistId: item.inspectionChecklistId,
      response,
      notes: next.notes || undefined,
    });
  }

  function handleNotes(item: ChecklistItem, notes: string) {
    if (checklist && isInspectionChecklistReadOnly(checklist.status)) return;
    setLocalStates((prev) => ({
      ...prev,
      [item.id]: { ...getLocalState(item), notes },
    }));
  }

  function handleSignOff() {
    signOff.mutate(
      { id },
      {
        onSuccess: () => {
          setSignOffOpen(false);
          void navigate({ to: "/inspections" });
        },
      },
    );
  }

  const isLoading = checklistLoading || itemsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-6 w-48" />
        </div>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-2 w-full rounded-full" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map((n) => (
            <Card key={n}>
              <CardContent className="p-4 space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <div className="flex gap-2">
                  <Skeleton className="h-11 flex-1" />
                  <Skeleton className="h-11 flex-1" />
                  <Skeleton className="h-11 flex-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (checklistError) {
    return (
      <ErrorState
        message={checklistErr?.message ?? "Failed to load inspection"}
        onRetry={() => refetchChecklist()}
      />
    );
  }

  if (itemsError) {
    return (
      <ErrorState
        message={itemsErr?.message ?? "Failed to load checklist items"}
        onRetry={() => refetchItems()}
      />
    );
  }

  if (!checklist) {
    return <div className="py-12 text-center text-muted-foreground">Inspection not found.</div>;
  }

  const allItems = items ?? [];
  const readOnly = isInspectionChecklistReadOnly(checklist.status);

  const respondedCount = allItems.filter((item) => {
    const state = getLocalState(item);
    return state.response !== null;
  }).length;

  const requiredItems = allItems.filter((item) => item.isRequired);
  const requiredComplete = requiredItems.every((item) => {
    const state = getLocalState(item);
    return state.response !== null;
  });

  const progressPct =
    allItems.length > 0 ? Math.round((respondedCount / allItems.length) * 100) : 0;

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center gap-3">
        <Link
          to="/inspections"
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md hover:bg-accent transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold truncate">{checklist.title}</h1>
          <p className="text-sm text-muted-foreground capitalize">
            {checklist.checklistType.replace(/_/g, " ")}
            {checklist.location && ` · ${checklist.location}`}
          </p>
        </div>
        <Badge variant={statusVariant(checklist.status)} className="shrink-0">
          {statusLabel(checklist.status)}
        </Badge>
      </div>

      {checklist.scheduledDate && (
        <p className="text-sm text-muted-foreground">
          Scheduled:{" "}
          {formatDateOnlyLocal(checklist.scheduledDate, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </p>
      )}

      {checklist.conductedAt && (
        <p className="text-sm text-muted-foreground">
          Conducted: {formatDate(checklist.conductedAt)}
        </p>
      )}

      {readOnly && (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          This inspection is signed off. You can review responses below; they cannot be changed.
        </div>
      )}

      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {respondedCount} of {allItems.length} items complete
          </span>
          <span className="font-medium">{progressPct}%</span>
        </div>
        <Progress value={progressPct} className="h-2" />
      </div>

      {allItems.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            No checklist items found for this inspection.
          </CardContent>
        </Card>
      )}

      {allItems.length > 0 && (
        <div className="space-y-3">
          {allItems.map((item) => (
            <ChecklistItemCard
              key={item.id}
              item={item}
              localState={getLocalState(item)}
              onResponse={(response) => handleResponse(item, response)}
              onNotes={(notes) => handleNotes(item, notes)}
              updatePending={updateItem.isPending}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}

      {allItems.length > 0 && !readOnly && (
        <div className="pt-2 pb-8">
          <Button
            className="w-full min-h-[48px] text-base"
            disabled={!requiredComplete || signOff.isPending}
            onClick={() => setSignOffOpen(true)}
          >
            {signOff.isPending ? "Signing Off..." : "Sign Off Inspection"}
          </Button>
          {!requiredComplete && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              All required items must have a response before signing off.
            </p>
          )}
        </div>
      )}

      {!readOnly && (
        <SignOffDialog
          open={signOffOpen}
          onOpenChange={setSignOffOpen}
          onConfirm={handleSignOff}
          isPending={signOff.isPending}
        />
      )}
    </div>
  );
}
